/**
 * Rückfrage beim Ablegen einer Mail mit Anhängen (User 03.11.2026: „Beim Drag &
 * Drop einer Mail … soll bei Mails mit Anhängen gefragt werden, ob die Anhänge
 * (zusätzlich) gespeichert werden sollen“).
 *
 * Ein gemeinsames Popup für alle Ablageziele — Kontakt-Dokumente und Alte
 * Kundenakte, Mail aus dem KMVS-Postfach und Mail-Datei aus Outlook. Eigenes
 * kvp-rd-Popup (`ConfirmDialog` über `imEigenenDialog`), nie ein Browser- oder
 * Fluent-Dialog; Texte bewusst einfach.
 *
 * Knöpfe:
 * - „Mail mit Anhängen“ (Hauptknopf) — bei mehreren Anhängen lassen sich
 *   einzelne abwählen; sind alle abgewählt, heißt der Knopf „Nur die Mail“.
 * - „Nur die Mail“ — links im Fuß.
 * - „Abbrechen“ (ESC, Klick daneben) — legt nichts ab. Bei einer Outlook-Mail
 *   liegt die Mail-Datei beim Fragen schon (der Browser kann eine `.msg` nicht
 *   selbst öffnen, gezählt wird nach dem Hochladen); dort gibt es kein
 *   Abbrechen, ESC heißt „Nur die Mail“.
 */
import * as React from "react";
import { useState } from "react";
import { ConfirmDialog } from "../../redesign/ConfirmDialog";
import { imEigenenDialog } from "../../redesign/bestaetigen/bestaetigen";
import { I } from "../../redesign/kvpIcons";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { Z_LAYERS } from "../../windowManager/zLayers";

export interface FrageAnhang {
    /** Eindeutig je Anhang — kommt in der Antwort zurück. */
    schluessel: string;
    name: string;
    groesse?: number | null;
}

export interface AnhangFrageOptionen {
    /** Betreff bzw. Dateiname der Mail. */
    mailName: string;
    anhaenge: FrageAnhang[];
    /**
     * Die Zahl ist unbekannt (verschlüsselte oder signierte Mail: Das Postfach
     * zeigt nur die Hülle). Dann ohne Liste fragen.
     */
    anzahlUnbekannt?: boolean;
    /**
     * Die Mail liegt schon (Outlook-Datei, nach dem Hochladen gezählt): kein
     * Abbrechen, gefragt wird nur noch nach den Anhängen.
     */
    mailLiegtSchon?: boolean;
}

export type AnhangWahl =
    | { art: "mitAnhaengen"; schluessel: string[]; alle: boolean }
    | { art: "nurMail" }
    | { art: "abbrechen" };

const anhaengeText = (n: number) => (n === 1 ? "1 Anhang" : `${n} Anhänge`);

/** Lesbare Größe („1,2 MB“). */
export function groesseText(bytes: number | null | undefined): string {
    if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

/** Beschriftung des Hauptknopfs für die aktuelle Auswahl. */
export function hauptknopfText(gewaehlt: number, gesamt: number, mailLiegtSchon: boolean): string {
    if (gesamt > 0 && gewaehlt === 0) return "Nur die Mail";
    if (mailLiegtSchon) {
        if (gewaehlt >= gesamt) return "Anhänge auch ablegen";
        return gewaehlt === 1 ? "1 Anhang ablegen" : `${gewaehlt} Anhänge ablegen`;
    }
    if (gewaehlt >= gesamt) return "Mail mit Anhängen";
    return gewaehlt === 1 ? "Mail mit 1 Anhang" : `Mail mit ${gewaehlt} Anhängen`;
}

const AnhangFrageDialog: React.FC<{ o: AnhangFrageOptionen; fertig: (wahl: AnhangWahl) => void }> = ({ o, fertig }) => {
    const alleSchluessel = o.anhaenge.map((a) => a.schluessel);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(() => new Set(alleSchluessel));
    const mitListe = !o.anzahlUnbekannt && o.anhaenge.length > 0;
    const mitHaken = mitListe && o.anhaenge.length > 1;
    const liegt = !!o.mailLiegtSchon;
    const gesamt = o.anhaenge.length;
    const anzahl = mitListe ? gewaehlt.size : gesamt;

    const umschalten = (s: string) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(s)) neu.delete(s);
            else neu.add(s);
            return neu;
        });

    const uebernehmen = () => {
        if (mitListe && gewaehlt.size === 0) {
            fertig({ art: "nurMail" });
            return;
        }
        const schluessel = mitListe ? alleSchluessel.filter((s) => gewaehlt.has(s)) : alleSchluessel;
        fertig({ art: "mitAnhaengen", schluessel, alle: !mitListe || schluessel.length === gesamt });
    };

    const frage = o.anzahlUnbekannt
        ? liegt
            ? "Die Mail ist abgelegt. Sie ist verschlüsselt oder signiert – ob sie Anhänge hat, zeigt sich erst beim Auspacken. Sollen ihre Anhänge zusätzlich als eigene Dateien abgelegt werden?"
            : "Diese Mail ist verschlüsselt oder signiert – ob sie Anhänge hat, zeigt sich erst beim Ablegen. Was soll abgelegt werden?"
        : liegt
          ? `Die Mail ist abgelegt. Sie hat ${anhaengeText(gesamt)}. Sollen ${gesamt === 1 ? "er" : "sie"} zusätzlich als eigene ${gesamt === 1 ? "Datei" : "Dateien"} abgelegt werden?`
          : `Diese Mail hat ${anhaengeText(gesamt)}. Was soll abgelegt werden?`;

    return (
        <ConfirmDialog
            open
            title={liegt ? "Anhänge auch ablegen?" : "Mail ablegen"}
            icon={I.mail}
            tone="primary"
            zIndex={Z_LAYERS.windowConfirm}
            message={
                <div data-kvp-anhang-frage="">
                    <div style={{ fontWeight: 700, color: REFINED.ink, marginBottom: 6, overflowWrap: "anywhere" }}>„{o.mailName}“</div>
                    <div style={{ marginBottom: mitListe ? 10 : 0 }}>{frage}</div>
                    {mitListe && (
                        <ul
                            aria-label="Anhänge"
                            style={{
                                listStyle: "none",
                                margin: 0,
                                padding: "4px 0",
                                maxHeight: 220,
                                overflowY: "auto",
                                border: `1px solid ${REFINED.line}`,
                                borderRadius: 10,
                                background: REFINED.fieldBg,
                            }}
                        >
                            {o.anhaenge.map((a) => (
                                <li key={a.schluessel} style={{ padding: "4px 10px" }}>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            cursor: mitHaken ? "pointer" : "default",
                                            color: REFINED.ink,
                                        }}
                                    >
                                        {mitHaken && (
                                            <input type="checkbox" checked={gewaehlt.has(a.schluessel)} onChange={() => umschalten(a.schluessel)} />
                                        )}
                                        <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{a.name}</span>
                                        {groesseText(a.groesse) && (
                                            <span style={{ color: REFINED.ink2, fontSize: REFINED.fs.xs, whiteSpace: "nowrap" }}>{groesseText(a.groesse)}</span>
                                        )}
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}
                    {mitHaken && (
                        <div style={{ marginTop: 6, fontSize: REFINED.fs.xs, color: REFINED.ink2 }}>
                            Haken weg = dieser Anhang wird nicht abgelegt.
                        </div>
                    )}
                </div>
            }
            confirmLabel={hauptknopfText(anzahl, mitListe ? gesamt : anzahl, liegt)}
            cancelLabel={liegt ? "Nein, nur die Mail" : "Abbrechen"}
            extraAction={liegt ? undefined : { label: "Nur die Mail", onClick: () => fertig({ art: "nurMail" }) }}
            onConfirm={uebernehmen}
            onCancel={() => fertig(liegt ? { art: "nurMail" } : { art: "abbrechen" })}
        />
    );
};

/**
 * Fragt, was von einer Mail mit Anhängen abgelegt werden soll. Ohne DOM
 * (Tests ohne jsdom, Server-Rendering) bleibt es beim bisherigen Verhalten:
 * Mail mit allen Anhängen.
 */
export function frageNachAnhaengen(o: AnhangFrageOptionen): Promise<AnhangWahl> {
    return imEigenenDialog<AnhangWahl>(
        { art: "mitAnhaengen", schluessel: o.anhaenge.map((a) => a.schluessel), alle: true },
        (fertig) => <AnhangFrageDialog o={o} fertig={fertig} />,
    );
}

export type AnhangFrage = typeof frageNachAnhaengen;
